/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-03-08 11:53:42
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Field, reduxForm } from 'redux-form';
import { Modal, FormLayout } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { connect } from 'react-redux';

import { RenderSelectMultiple } from 'src/components/views/common/forms/render-select-multiple';
import { RenderCheckbox2 } from 'src/components/views/common/forms/render-checkbox-2';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { startSABLoading, stopSABLoading } from 'src/integrations/shopify-app-bridge/loading';
import { isRequired, isEmail } from 'src/helpers/form-validation-helper';
import { featureReports, typeOfIssue } from 'src/constants/feedback-options';
import { prettyText } from 'src/helpers';

import * as suportTicketActions from 'actions/support-ticket-actions';

const isRequiredValue = isRequired();
const isEmailValue = isEmail();

type FeedbackType = {
  email: string,
  content: string,
  selection: { code: string, codeDescription: string }
};

type Props = {
  success: boolean,
  loading: boolean,
  reset: () => void,
  resetSendSupportTicket: () => void,
  handleChangeModal: () => void,
  activeSendRequestModal: () => void,
  handleSubmit: Function => void,
  sendSupportTicket: (feedback: FeedbackType, files: string[]) => void,
  pristine: boolean,
  submitting: boolean,
  requestModalThemeFailed: boolean
};

type ComposedProps = Props & WithI18nProps;

class SendRequestReport extends Component<ComposedProps> {
  componentWillReceiveProps(nextProps) {
    if (nextProps.success === true && nextProps.activeSendRequestModal === true) {
      // Reset form
      this.props.reset();

      // Reset all states
      this.props.resetSendSupportTicket();

      // Close modal
      this.props.handleChangeModal();
      this.props.handleAfterSend();
    }

    if (nextProps.loading === true && this.props.loading === false) {
      startSABLoading();
    }

    if (nextProps.loading === false && this.props.loading === true) {
      stopSABLoading();
    }
  }

  render() {
    const {
      activeSendRequestModal,
      handleChangeModal,
      loading,
      handleSubmit,
      pristine,
      submitting,
      requestModalThemeFailed,
      i18n
    } = this.props;

    const formattedCustomizeChoice = featureReports.map(function (option) {
      return {
        ...option,
        label: i18n.translate(option.label)
      };
    });

    const formattedCustomizeSelect = typeOfIssue.map(function (option) {
      return {
        ...option,
        label: i18n.translate(option.label)
      };
    });

    return (
      <Modal
        open={activeSendRequestModal}
        onClose={handleChangeModal}
        title={i18n.translate('AdminHTML.DashboardV3.InstallStatusBanner.sendRequestModal.title')}
        primaryAction={{
          content: i18n.translate('AdminHTML.Common.Action.sendRequest'),
          disabled: pristine || submitting,
          loading: loading && !pristine,
          onAction: handleSubmit(this.handleSave.bind(this))
        }}
        secondaryActions={[
          {
            content: i18n.translate('AdminHTML.Common.Action.cancel'),
            onAction: handleChangeModal
          }
        ]}>
        <Modal.Section>
          <p>
            {i18n.translate(
              `AdminHTML.DashboardV3.InstallStatusBanner.sendRequestModal.subContent${
                requestModalThemeFailed ? 'ThemeFailed' : ''
              }`
            )}
          </p>
          <form className="BC-SendRequestReport__Form" onSubmit={this.handleDefaultSubmit}>
            <FormLayout>
              <Field
                name="email"
                label={i18n.translate('AdminHTML.Common.FieldLabel.yourEmail')}
                placeholder={i18n.translate('AdminHTML.Common.Placeholder.email')}
                component={RenderTextField}
                validate={[isRequiredValue, isEmailValue]}
              />
              {!requestModalThemeFailed && (
                <Field
                  name="features"
                  label={i18n.translate(
                    'AdminHTML.DashboardV3.InstallStatusBanner.sendRequestModal.labelChoice'
                  )}
                  component={RenderCheckbox2}
                  options={formattedCustomizeChoice}
                  validate={[isRequiredValue]}
                />
              )}
              {!requestModalThemeFailed && (
                <Field
                  name="typeOfIssue"
                  component={RenderSelectMultiple}
                  options={formattedCustomizeSelect}
                  label={i18n.translate(
                    'AdminHTML.DashboardV3.InstallStatusBanner.sendRequestModal.labelSelectType'
                  )}
                  placeholder={i18n.translate(
                    'AdminHTML.DashboardV3.InstallStatusBanner.sendRequestModal.categoryPlaceholder'
                  )}
                />
              )}

              <Field
                name="content"
                label={i18n.translate(
                  'AdminHTML.DashboardV3.InstallStatusBanner.sendRequestModal.labelSelectDetail'
                )}
                placeholder={i18n.translate(
                  `AdminHTML.DashboardV3.InstallStatusBanner.sendRequestModal.detailPlaceholder${
                    requestModalThemeFailed ? 'ThemeFailed' : ''
                  }`
                )}
                component={RenderTextField}
                anotherProps={{ multiline: 5 }}
              />
            </FormLayout>
          </form>
        </Modal.Section>
      </Modal>
    );
  }

  // Handle default form submit
  handleDefaultSubmit(e) {
    e.preventDefault();
  }

  // Validate value
  validateSubmit = data => {
    return true;
  };

  //Handle submit
  handleSave = data => {
    if (this.validateSubmit(data)) {
      const { sendSupportTicket } = this.props;
      const { email, features, content, typeOfIssue } = _.pick(data, [
        'features',
        'email',
        'typeOfIssue',
        'content'
      ]);

      const feedback = {
        email: prettyText(email),
        content: prettyText(content),
        customization: typeOfIssue,
        selection: features
      };

      sendSupportTicket(feedback);
    }
  };
}

const mapStateToProps = (state, props) => {
  const tenantApp = _.get(state.oauth, 'tenantApp', {});

  return {
    loading: state.supportTicket.sendSupportTicketLoading,
    success: state.supportTicket.sendSupportTicketSuccess,
    error: state.supportTicket.error,
    initialValues: { storeId: tenantApp.tenantId }
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  sendSupportTicket: (body: Object) =>
    dispatch(suportTicketActions.sendSupportTicket(body, [], ownProps.i18n)),
  resetSendSupportTicket: () => dispatch(suportTicketActions.resetSendSupportTicket())
});

const SendRequestReportForm = reduxForm({
  form: 'SendRequestReport', // a unique identifier for this form
  enableReinitialize: true
})(SendRequestReport);

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(SendRequestReportForm));
